import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronsUp, ChevronsDown } from 'lucide-react'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { PriceRangeFormField } from 'src/shared/components/ui/forms/form-fields/price-range'
import { SelectFormField } from 'src/shared/components/ui/forms/form-fields/select'
import { TextareaFormField } from 'src/shared/components/ui/forms/form-fields/textarea'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { propertyTypeMap, propertyTypeSchema } from 'src/shared/schemas/referrals/referral-fields/shared/property-type'
import { timeframeMap, timeframeSchema } from 'src/shared/schemas/referrals/referral-fields/shared/timeframe'
import { useEventStore } from 'src/shared/store/event-store'

const insuranceBrokerSchema = z.object({
  priceRange: z.object({
    min: z.number().min(0),
    max: z.number().min(0),
  }),
  propertyType: propertyTypeSchema,
  // Optional fields
  timeframe: timeframeSchema.optional(),
  dwellingCoverage: z.number().optional(),
  liabilityCoverage: z.number().min(0).optional(),
  personalPropertyCoverage: z.number().min(0).optional(),
  deductibleAmount: z.number().min(0).optional(),
  additionalEndorsements: z.number().min(0).optional(),
  carrierName: z.string().optional(),
  coverageLevelDetails: z.string().max(1000).optional(),
  pastClaims: z
    .array(
      z.object({
        type: z.string(),
        amount: z.number().min(0),
      }),
    )
    .optional(),
})

type InsuranceBrokerFormData = z.infer<typeof insuranceBrokerSchema>

export const InsuranceBrokerInfo: React.FC = () => {
  const handleNext = useSendReferralStore((state) => state.handleNext)
  const handleBack = useSendReferralStore((state) => state.handleBack)
  const selectedReferral = useSendReferralStore((state) => state.selectedReferral)
  const referralFormData = useSendReferralStore((state) => state.referralFormData)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId
  const [showOptional, setShowOptional] = useState(false)

  const form = useForm<InsuranceBrokerFormData>({
    resolver: zodResolver(insuranceBrokerSchema),
    mode: 'onSubmit',
    defaultValues: {
      priceRange: {
        min: referralFormData.priceRange?.min || selectedReferral?.priceRange?.min || 100000,
        max: referralFormData.priceRange?.max || selectedReferral?.priceRange?.max || 450000,
      },
      propertyType: referralFormData.propertyType || selectedReferral?.propertyType || undefined,
      timeframe: referralFormData.timeframe || selectedReferral?.timeframe || undefined,
      dwellingCoverage: referralFormData.dwellingCoverage || selectedReferral?.dwellingCoverage || undefined,
      liabilityCoverage: referralFormData.liabilityCoverage || selectedReferral?.liabilityCoverage || undefined,
      personalPropertyCoverage:
        referralFormData.personalPropertyCoverage || selectedReferral?.personalPropertyCoverage || undefined,
      deductibleAmount: referralFormData.deductibleAmount || selectedReferral?.deductibleAmount || undefined,
      additionalEndorsements:
        referralFormData.additionalEndorsements || selectedReferral?.additionalEndorsements || undefined,
      carrierName: referralFormData.carrierName || selectedReferral?.carrierName || undefined,
      coverageLevelDetails:
        referralFormData.coverageLevelDetails || selectedReferral?.coverageLevelDetails || undefined,
      pastClaims: referralFormData.pastClaims || selectedReferral?.pastClaims || [],
    },
  })

  const onSubmit = async (data: InsuranceBrokerFormData) => {
    if (currentUser) {
      sendEvent({
        userId: currentUser.id,
        eventType: 'flow_step',
        info: {
          sessionId,
          flowType: 'send_referral',
          flowStep: 'submit_insurance_broker_info',
          flowId,
          ...data,
        },
      })
    }

    handleNext(data)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mb-24 space-y-8">
        <PriceRangeFormField control={form.control} name="priceRange" label="Price Range" required />
        <SelectFormField control={form.control} name="propertyType" label="Property Type" options={propertyTypeMap} />

        <Button
          variant="link"
          type="button"
          onClick={() => setShowOptional(!showOptional)}
          className="flex w-full items-center justify-center gap-2 text-muted hover:text-muted"
        >
          {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />} Optional Info{' '}
          {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />}
        </Button>

        {showOptional && (
          <>
            <SelectFormField
              control={form.control}
              name="timeframe"
              label="Timeframe for coverage"
              options={timeframeMap}
            />
            <InputFormField
              control={form.control}
              name="dwellingCoverage"
              label="Dwelling Coverage"
              type="currency"
              placeholder="Enter Amount"
              floatingLabel={false}
              switch={{ defaultChecked: false }}
            />
            <InputFormField
              control={form.control}
              name="liabilityCoverage"
              label="Liability Coverage"
              type="currency"
              placeholder="Enter Amount"
              floatingLabel={false}
              switch={{ defaultChecked: false }}
            />
            <InputFormField
              control={form.control}
              name="personalPropertyCoverage"
              label="Personal Property Coverage"
              type="currency"
              placeholder="Enter Amount"
              floatingLabel={false}
              switch={{ defaultChecked: false }}
            />
            <InputFormField
              control={form.control}
              name="deductibleAmount"
              label="Deductible Amount"
              type="currency"
              placeholder="Enter Amount"
              floatingLabel={false}
              switch={{ defaultChecked: false }}
            />

            <InputFormField
              control={form.control}
              name="additionalEndorsements"
              label="Additional Endorsements"
              type="currency"
              placeholder="Enter Amount"
              floatingLabel={false}
              switch={{ defaultChecked: false }}
            />

            <InputFormField
              control={form.control}
              name="carrierName"
              label="Current Carrier Name"
              placeholder="Enter Name"
              floatingLabel={false}
              checkbox={{ label: 'Not applicable', defaultChecked: false }}
            />

            <TextareaFormField
              control={form.control}
              name="coverageLevelDetails"
              label="Coverage Level Details"
              description="Enter..."
              maxLength={1000}
            />

            {/* TODO: pastClaims array field needs a custom component for handling multiple claims */}
          </>
        )}

        <FixedFooter className="fixed inset-x-0">
          <div className="flex w-full gap-3">
            <Button variant="link" type="button" onClick={handleBack} className="w-full">
              Previous
            </Button>
            <Button type="submit" className="w-full">
              Next
            </Button>
          </div>
        </FixedFooter>
      </form>
    </Form>
  )
}
